/* v1.5: adds product-line engine-type layer before HP group filtering. */
/*
  Wakeboss Mercury EPC Result Builder v1.0

  EPC-first result builder:
  Serial -> EPC catalog -> model -> exact mapped parts pages -> kit components -> supersession -> Shopify live status

  This intentionally avoids broad maintenance-fitment guessing. v1.0 adds model/applicability-aware parts-page filtering and ignores discontinued/NSS EPC rows.
*/
(function () {
  class WBMercuryEpcResultBuilder {
    constructor(options) {
      this.catalogIndex = options.catalogIndex || {};
      this.partsPages = options.partsPages || {};
      this.supersessionMap = options.supersessionMap || {};
      this.partsIndex = options.partsIndex || {};
    }

    lookupCatalogsBySerial(serial) {
      const normalized = this.normalizeSerial(serial);
      const parsed = this.parseSerial(normalized);

      if (!parsed) {
        return {
          entered_serial: serial,
          entered_serial_normalized: normalized,
          matches: [],
          lookup_status: 'invalid_serial'
        };
      }

      const catalogs = this.catalogIndex.catalogs || [];

      // Exact EPC screenshot examples are authoritative. If a serial was captured
      // from EPC, return only that catalog/model family before considering broad
      // open-ended ranges like 2B529482 & Up.
      const exactMatches = catalogs.filter((catalog) => {
        const exactExamples = (catalog.serial_examples || []).map((item) => this.normalizeSerial(item));
        return exactExamples.includes(normalized);
      });

      if (exactMatches.length) {
        return {
          entered_serial: serial,
          entered_serial_normalized: normalized,
          lookup_status: 'exact_catalog_match',
          matches: exactMatches
        };
      }

      const matches = catalogs.filter((catalog) => {
        return (catalog.serial_ranges || []).some((range) => this.rangeContains(range, parsed));
      });

      const filtered = this.filterMostSpecificCatalogs(matches, parsed, normalized);

      return {
        entered_serial: serial,
        entered_serial_normalized: normalized,
        lookup_status: filtered.length ? 'catalog_matches' : 'no_catalog_match',
        matches: filtered
      };
    }


    getProductLineSearchOptions(productLine, engineType) {
      const normalizedProductLine = this.normalizeFilterValue(productLine);
      const normalizedEngineType = this.normalizeFilterValue(engineType);
      const engineTypes = new Set();
      const hpGroups = new Set();
      const displacements = new Set();
      const years = new Set();

      (this.catalogIndex.catalogs || []).forEach((catalog) => {
        const mappedModels = this.getMappedModelsForCatalog(catalog);
        if (!mappedModels.length) return;
        if (normalizedProductLine && this.normalizeFilterValue(catalog.product_line) !== normalizedProductLine) return;

        const catalogEngineType = this.catalogEngineType(catalog);
        if (catalogEngineType) engineTypes.add(catalogEngineType);

        if (normalizedEngineType && this.normalizeFilterValue(catalogEngineType) !== normalizedEngineType) return;

        mappedModels.forEach((model) => {
          this.modelHpGroups(model).forEach((value) => hpGroups.add(value));
        });
        this.catalogDisplacements(catalog).forEach((value) => displacements.add(value));
        this.catalogYears(catalog).forEach((value) => years.add(value));
      });

      return {
        engine_types: this.sortEngineTypes(Array.from(engineTypes)),
        hp_groups: this.sortMixed(Array.from(hpGroups)),
        displacements: this.sortMixed(Array.from(displacements)),
        years: this.sortMixed(Array.from(years))
      };
    }

    getMappedProductLines() {
      const labels = {
        outboard: 'Outboard',
        inboard_and_towsports: 'Inboard and TowSports',
        jet_drive_sportjet: 'Jet Drive - SportJet',
        sterndrive: 'Sterndrive'
      };
      const preferred = ['outboard', 'inboard_and_towsports', 'jet_drive_sportjet', 'sterndrive'];
      const found = new Set();

      (this.catalogIndex.catalogs || []).forEach((catalog) => {
        if (!this.catalogHasMappedData(catalog)) return;
        const value = this.normalizeFilterValue(catalog.product_line);
        if (value) found.add(value);
      });

      return preferred.filter((value) => found.has(value)).map((value) => ({
        value: value,
        label: labels[value] || value
      }));
    }

    lookupCatalogsByProductFilters(filters) {
      const productLine = this.normalizeFilterValue(filters?.product_line);
      const engineType = this.normalizeFilterValue(filters?.engine_type);
      const hpGroup = this.normalizeFilterValue(filters?.hp_group);
      const displacement = this.normalizeFilterValue(filters?.displacement);
      const year = this.normalizeFilterValue(filters?.year);

      const allowedProductLines = new Set(['outboard', 'inboard_and_towsports', 'jet_drive_sportjet', 'sterndrive']);

      const matches = (this.catalogIndex.catalogs || []).reduce((out, catalog) => {
        const mappedModels = this.getMappedModelsForCatalog(catalog);
        if (!mappedModels.length) return out;
        const catalogProductLine = this.normalizeFilterValue(catalog.product_line);
        if (!allowedProductLines.has(catalogProductLine)) return out;
        if (productLine && catalogProductLine !== productLine) return out;

        if (engineType) {
          const catalogEngineType = this.normalizeFilterValue(this.catalogEngineType(catalog));
          if (catalogEngineType !== engineType) return out;
        }

        let modelMatches = mappedModels;
        if (hpGroup) {
          modelMatches = modelMatches.filter((model) => {
            return this.modelHpGroups(model).map((value) => this.normalizeFilterValue(value)).includes(hpGroup);
          });
          if (!modelMatches.length) return out;
        }

        if (displacement) {
          const displacementCandidates = this.catalogDisplacements(catalog).map((value) => this.normalizeFilterValue(value));
          if (displacementCandidates.length && !displacementCandidates.includes(displacement)) return false;
        }

        if (year) {
          const yearCandidates = this.catalogYears(catalog).map((value) => this.normalizeFilterValue(value));
          if (yearCandidates.length && !yearCandidates.includes(year)) return out;
        }

        out.push(Object.assign({}, catalog, { models: modelMatches }));
        return out;
      }, []);

      return {
        lookup_status: matches.length ? 'catalog_matches' : 'no_catalog_match',
        filters: { product_line: productLine, engine_type: engineType, hp_group: hpGroup, displacement, year },
        matches
      };
    }

    catalogEngineType(catalog) {
      if (catalog.engine_type) return String(catalog.engine_type);
      if (catalog.engine_type_key) return String(catalog.engine_type_key);

      const labels = (catalog.models || []).map((model) => String(model.model_label || '')).join(' ').toUpperCase();
      const family = String(catalog.engine_family || '').toUpperCase();
      const architecture = String(catalog.engine_architecture || '').toUpperCase();

      if (labels.includes('OPTIMAX') || family.includes('OPTIMAX')) return '2 Stroke DFI (OptiMax)';
      if (labels.includes('DFI') && !labels.includes('4-STROKE')) return '2 Stroke DFI (OptiMax)';
      if ((labels.includes('4-STROKE') || labels.includes('FOURSTROKE') || architecture.includes('V6') || architecture.includes('V8') || architecture.includes('V10') || architecture.includes('V12')) && String(catalog.product_line || '').toLowerCase() === 'outboard') return '4 Stroke EFI';
      return '';
    }

    sortEngineTypes(values) {
      const preferred = [
        '4 Stroke EFI',
        '4 Stroke Carb',
        'Electric',
        '2 Stroke DFI (OptiMax)',
        '2 Stroke EFI',
        '2 Stroke Carb',
        'Mark & K Series (pre 1960)',
        'Force',
        'Chrysler',
        'Sears'
      ];
      const index = new Map(preferred.map((value, idx) => [this.normalizeFilterValue(value), idx]));
      return (values || []).slice().sort((a, b) => {
        const ia = index.has(this.normalizeFilterValue(a)) ? index.get(this.normalizeFilterValue(a)) : 999;
        const ib = index.has(this.normalizeFilterValue(b)) ? index.get(this.normalizeFilterValue(b)) : 999;
        if (ia !== ib) return ia - ib;
        return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
      });
    }

    catalogHpGroups(catalog) {
      const out = new Set();
      (catalog.hp_groups || []).forEach((value) => { if (value) out.add(String(value)); });
      (catalog.models || []).forEach((model) => {
        this.modelHpGroups(model).forEach((value) => out.add(value));
      });
      return Array.from(out).filter(Boolean);
    }

    modelHpGroups(model) {
      const out = new Set();
      if (model?.hp_group) out.add(String(model.hp_group));
      if (model?.horsepower) out.add(String(model.horsepower));
      const label = String(model?.model_label || '').toUpperCase();
      const hpMatch = label.match(/^(\d{1,3})(R|EFI|DFI|JET)?\b/);
      if (hpMatch) {
        out.add(hpMatch[1] + (hpMatch[2] || ''));
        if (!hpMatch[2] || hpMatch[2] !== 'JET') out.add(hpMatch[1]);
      }
      return Array.from(out).filter(Boolean);
    }

    catalogDisplacements(catalog) {
      const out = new Set();
      (catalog.displacement_groups || []).forEach((value) => { if (value) out.add(String(value)); });
      const arch = String(catalog.engine_architecture || '');
      const litre = arch.match(/(\d+(?:\.\d+)?)\s*L/i);
      if (litre) out.add(litre[1]);
      (catalog.models || []).forEach((model) => {
        const label = String(model.model_label || '');
        const match = label.match(/(\d+(?:\.\d+)?)\s*L/i);
        if (match) out.add(match[1]);
      });
      return Array.from(out).filter(Boolean);
    }

    catalogYears(catalog) {
      const out = new Set();
      (catalog.year_options || catalog.years || catalog.model_years || []).forEach((value) => { if (value) out.add(String(value)); });
      return Array.from(out).filter(Boolean);
    }

    normalizeFilterValue(value) {
      return String(value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    }

    sortMixed(values) {
      return (values || []).slice().sort((a, b) => {
        const na = parseFloat(a);
        const nb = parseFloat(b);
        const aNum = !Number.isNaN(na) && String(a).match(/^\d+(?:\.\d+)?/);
        const bNum = !Number.isNaN(nb) && String(b).match(/^\d+(?:\.\d+)?/);
        if (aNum && bNum && na !== nb) return na - nb;
        return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
      });
    }


    modelIdsForSerial(catalog, serial) {
      const normalized = this.normalizeSerial(serial);
      const serialMap = catalog?.serial_model_examples || {};
      const output = new Set();

      Object.keys(serialMap || {}).forEach((key) => {
        if (this.normalizeSerial(key) !== normalized) return;
        const value = serialMap[key];
        if (Array.isArray(value)) {
          value.forEach((item) => { if (item) output.add(String(item)); });
        } else if (value) {
          output.add(String(value));
        }
      });

      return output;
    }

    filterModelsForSerial(catalog, serial) {
      const models = catalog?.models || [];
      const allowed = this.modelIdsForSerial(catalog, serial);
      if (!allowed.size) return models;
      return models.filter((model) => allowed.has(model.model_id));
    }


    buildCatalogChoiceCards(catalogs, serial) {
      const cards = [];

      (catalogs || []).forEach((catalog) => {
        this.filterModelsForSerial(catalog, serial).forEach((model) => {
          cards.push({
            type: 'catalog_model_choice',
            catalog_id: catalog.catalog_id,
            catalog_number: catalog.catalog_number,
            serial_label: (catalog.serial_ranges || [])[0]?.serial_label || '',
            model_id: model.model_id,
            model_label: model.model_label,
            model_number: model.model_number || null,
            horsepower: model.horsepower || null,
            engine_family: catalog.engine_family || '',
            engine_architecture: catalog.engine_architecture || '',
            mapping_status: catalog.mapping_status || 'unknown'
          });
        });
      });

      return cards;
    }


    getCategoriesForCatalogModel(catalogId, modelId, serial) {
      const catalog = (this.catalogIndex.catalogs || []).find((item) => item.catalog_id === catalogId);
      const model = (catalog?.models || []).find((item) => item.model_id === modelId);
      if (!catalog || !model) return [];

      const pageIds = this.getCatalogPartsPageIds(catalog);
      const applicablePages = (this.partsPages.parts_pages || []).filter((page) => {
        return pageIds.includes(page.parts_page_id) && this.partsPageApplies(page, model, serial);
      });

      const pageByCategory = new Map();
      applicablePages.forEach((page) => {
        if (!page.category_id) return;
        if (!pageByCategory.has(page.category_id)) pageByCategory.set(page.category_id, []);
        pageByCategory.get(page.category_id).push(page);
      });

      const categories = (catalog.epc_categories || [])
        .filter((cat) => cat.status !== 'internal_only')
        .map((cat) => {
          const pages = pageByCategory.get(cat.category_id) || [];
          const mappedItemCount = pages.reduce((sum, page) => {
            return sum + (page.items || []).filter((row) => {
              return row && !['show_under_current_part', 'do_not_display'].includes(row.display_role);
            }).length;
          }, 0);
          return {
            category_id: cat.category_id,
            label: cat.label,
            status: pages.length ? 'mapped' : (cat.status || 'capture_needed'),
            parts_page_ids: pages.map((page) => page.parts_page_id),
            mapped_item_count: mappedItemCount
          };
        });

      return categories.sort((a, b) => {
        if (a.status === 'mapped' && b.status !== 'mapped') return -1;
        if (b.status === 'mapped' && a.status !== 'mapped') return 1;
        return String(a.label).localeCompare(String(b.label), undefined, { numeric: true, sensitivity: 'base' });
      });
    }

    buildResultForCatalogModelCategory(catalogId, modelId, categoryId, serial) {
      const catalog = (this.catalogIndex.catalogs || []).find((item) => item.catalog_id === catalogId);
      const model = (catalog?.models || []).find((item) => item.model_id === modelId);
      if (!catalog || !model) {
        return { lookup_status: 'missing_catalog_or_model', recommended_sections: [], warnings: ['Selected catalog or model was not found.'] };
      }

      const result = this.buildBaseResult(catalog, model, serial);
      result.input_context.selected_category_id = categoryId || null;

      const pageIds = this.getCatalogPartsPageIds(catalog);
      const pages = (this.partsPages.parts_pages || []).filter((page) => {
        return pageIds.includes(page.parts_page_id) &&
          page.category_id === categoryId &&
          this.partsPageApplies(page, model, serial);
      });

      if (!pages.length) {
        result.lookup_status = 'category_recognized_parts_not_mapped';
        result.warnings.push('This EPC category is recognized, but the exact parts page is not mapped yet.');
        return result;
      }

      const sectionsByPage = [];
      pages.forEach((page) => {
        const section = {
          section_id: 'epc-category-' + (page.category_id || page.parts_page_id),
          section_title: page.category_label || page.page_title || 'EPC Parts',
          section_type: 'epc_category_parts',
          diagram: page.diagram || null,
          items: []
        };
        (page.items || []).forEach((row) => {
          const display = this.rowToDisplayItem(row, page, catalog, model);
          if (display) section.items.push(display);
        });
        section.items = this.dedupeAndApplySupersessions(section.items);
        if (section.items.length) sectionsByPage.push(section);
      });

      result.recommended_sections = sectionsByPage;
      if (!sectionsByPage.length) {
        result.lookup_status = 'category_mapped_no_displayable_parts';
        result.warnings.push('This EPC category is mapped, but all captured rows are hidden, discontinued, or not sold separately.');
      }
      return result;
    }

    buildBaseResult(catalog, model, serial) {
      return {
        lookup_status: 'matched',
        generated_at: new Date().toISOString(),
        input_context: {
          entered_serial: serial || null,
          selected_catalog_id: catalog.catalog_id,
          selected_model_id: model.model_id
        },
        matched_identity: {
          catalog_id: catalog.catalog_id,
          catalog_number: catalog.catalog_number,
          serial_label: (catalog.serial_ranges || [])[0]?.serial_label || '',
          model_id: model.model_id,
          model_label: model.model_label,
          model_number: model.model_number || null,
          engine_family: catalog.engine_family,
          engine_architecture: catalog.engine_architecture,
          mapping_status: catalog.mapping_status
        },
        selected_catalog_choice: {
          catalog_id: catalog.catalog_id,
          catalog_number: catalog.catalog_number,
          model_id: model.model_id,
          model_label: model.model_label,
          serial_label: (catalog.serial_ranges || [])[0]?.serial_label || ''
        },
        recommended_sections: [],
        warnings: []
      };
    }
    buildResultForCatalogModel(catalogId, modelId, serial) {
      const catalog = (this.catalogIndex.catalogs || []).find((item) => item.catalog_id === catalogId);
      const model = (catalog?.models || []).find((item) => item.model_id === modelId);

      if (!catalog || !model) {
        return {
          lookup_status: 'missing_catalog_or_model',
          recommended_sections: [],
          warnings: ['Selected catalog or model was not found.']
        };
      }

      const result = this.buildBaseResult(catalog, model, serial);

      const pageIds = this.getCatalogPartsPageIds(catalog);
      const pages = (this.partsPages.parts_pages || []).filter((page) => pageIds.includes(page.parts_page_id) && this.partsPageApplies(page, model, serial));

      if (!pages.length) {
        result.lookup_status = 'catalog_recognized_parts_not_mapped';
        result.warnings.push('This catalog/model is recognized, but exact EPC parts pages are not mapped yet.');
        return result;
      }

      const sections = {
        recommended_kits: {
          section_id: 'recommended-kits',
          section_title: 'Recommended Kits',
          section_type: 'recommended_kits',
          items: []
        },
        standalone_service_parts: {
          section_id: 'standalone-service-parts',
          section_title: 'Standalone Service Parts',
          section_type: 'standalone_service_parts',
          items: []
        },
        kit_components: {
          section_id: 'kit-components',
          section_title: 'Included Kit Components',
          section_type: 'kit_components',
          items: []
        },
        verify_only: {
          section_id: 'verify-before-ordering',
          section_title: 'Verify Before Ordering',
          section_type: 'verify_only',
          items: []
        },
        category_parts: {
          section_id: 'epc-category-parts',
          section_title: 'EPC Category Parts',
          section_type: 'epc_category_parts',
          items: []
        }
      };

      pages.forEach((page) => {
        (page.items || []).forEach((row) => {
          const display = this.rowToDisplayItem(row, page, catalog, model);
          if (!display) return;

          if (display.display_role === 'recommended_kit' || display.display_role === 'recommended_kit_researched') {
            sections.recommended_kits.items.push(display);
          } else if (display.display_role === 'standalone_service_part' || display.display_role === 'kit_component_and_standalone') {
            sections.standalone_service_parts.items.push(display);
          } else if (display.display_role === 'kit_component' || display.display_role === 'kit_component_superseded_standalone') {
            sections.kit_components.items.push(display);
          } else if (display.display_role === 'verify_only') {
            sections.verify_only.items.push(display);
          } else if (display.display_role === 'epc_category_part' || display.display_role === 'not_sold_separately') {
            sections.category_parts.items.push(display);
          }
        });
      });

      // Superseded current-part behavior
      Object.keys(sections).forEach((key) => {
        sections[key].items = this.dedupeAndApplySupersessions(sections[key].items);
      });

      result.recommended_sections = Object.values(sections).filter((section) => section.items.length > 0);

      return result;
    }

    partsPageApplies(page, model, serial) {
      if (!page) return false;

      const modelIds = page.applicable_model_ids || [];
      if (modelIds.length && !modelIds.includes(model.model_id)) return false;

      const applicability = page.applicability || {};
      const normalizedSerial = this.normalizeSerial(serial);
      if (normalizedSerial) {
        const parsed = this.parseSerial(normalizedSerial);
        if (parsed && applicability.usa_serial_start) {
          const start = this.parseSerial(applicability.usa_serial_start);
          if (start && this.compareParsedSerials(parsed, start) < 0) return false;
        }
        if (parsed && applicability.usa_serial_end) {
          const end = this.parseSerial(applicability.usa_serial_end);
          if (end && this.compareParsedSerials(parsed, end) > 0) return false;
        }
      }

      return true;
    }

    rowToDisplayItem(row, page, catalog, model) {
      if (!row || row.display_role === 'hidden_for_standard_verado' || row.display_role === 'show_under_current_part' || row.display_role === 'do_not_display') return null;

      const rawPart = this.normalizeSku(row.part_number);
      const currentPart = this.normalizeSku(row.current_part_number || this.currentPartFor(rawPart, catalog, page));
      const isNotSoldSeparately = row.display_role === 'not_sold_separately' || row.epc_hierarchy_role === 'not_sold_separate' || rawPart === 'NSS';
      const displayPart = isNotSoldSeparately
        ? `NSS-${page.parts_page_id || page.category_id || 'epc'}-${row.row_number || row.epc_source_order || 'row'}-${row.epc_source_order || ''}`
        : (currentPart || rawPart);

      if (!displayPart) return null;

      const partRecord = isNotSoldSeparately ? null : this.findPartRecord(displayPart);

      let recommendationStatus = 'special_order';
      let customerAction = 'request_part';
      let buttonLabel = 'Special Order This Part';

      if (isNotSoldSeparately) {
        recommendationStatus = 'not_sold_separately';
        customerAction = 'not_sold_separately';
        buttonLabel = 'Not Sold Separately';
      }

      if (row.display_role === 'verify_only') {
        recommendationStatus = 'dealer_verify';
        customerAction = 'request_verification';
        buttonLabel = 'Verify This Part';
      }

      if (row.epc_availability === 'no_longer_available') {
        recommendationStatus = 'no_longer_available';
        customerAction = 'no_longer_available';
        buttonLabel = 'No Longer Available';
      }

      const item = {
        wakeboss_sku: displayPart,
        core_part_number: displayPart,
        part_number: displayPart,
        original_part_number: rawPart !== displayPart ? rawPart : null,
        part_number_display: isNotSoldSeparately ? 'NSS' : displayPart,
        part_description: partRecord?.part_description || partRecord?.description || row.description || displayPart,
        label: partRecord?.label || row.description || displayPart,
        quantity: row.quantity || null,
        diagram_number: row.diagram_number || ((row.row_number && row.row_number !== '-') ? row.row_number : null),
        epc_row_number: row.row_number || null,
        epc_source_order: row.epc_source_order || null,
        display_role: row.display_role || null,
        section_type: page.page_type || null,
        recommendation_status: recommendationStatus,
        customer_action: customerAction,
        button_label: buttonLabel,
        service_intervals: this.serviceIntervalsFromRow(row),
        included_in_kits: row.included_in_kits || [],
        included_in_parents: row.included_in_parents || [],
        kit_contains_parts: row.kit_contains_parts || [],
        epc_hierarchy_role: row.epc_hierarchy_role || null,
        indent_level: row.indent_level || 0,
        parent_type: row.parent_type || null,
        epc_comments: row.comments || null,
        epc_availability: row.epc_availability || null,
        kit_role: row.kit_role || null,
        kit_group_id: row.kit_group_id || null,
        supersession_chain: [],
        source_refs: [
          {
            catalog_number: catalog.catalog_number,
            page_title: page.page_title,
            parts_page_id: page.parts_page_id,
            row_part_number: row.part_number,
            diagram_number: row.diagram_number || ((row.row_number && row.row_number !== '-') ? row.row_number : null),
            comments: row.comments || null
          }
        ]
      };

      if (!isNotSoldSeparately && rawPart && rawPart !== displayPart) {
        item.supersession_chain.push({
          old_part_number: rawPart,
          current_part_number: displayPart,
          note: 'Current replacement shown; old number kept for reference.'
        });
      }

      return item;
    }

    dedupeAndApplySupersessions(items) {
      const bySku = new Map();

      (items || []).forEach((item) => {
        const sku = this.normalizeSku(item.wakeboss_sku || item.part_number);
        if (!sku) return;

        if (!bySku.has(sku)) {
          bySku.set(sku, item);
          return;
        }

        const existing = bySku.get(sku);
        existing.source_refs = [...(existing.source_refs || []), ...(item.source_refs || [])];
        existing.supersession_chain = this.uniqueSupersessionChain([...(existing.supersession_chain || []), ...(item.supersession_chain || [])]);
        existing.service_intervals = Array.from(new Set([...(existing.service_intervals || []), ...(item.service_intervals || [])]));
        if (item.epc_source_order != null) existing.epc_source_order = existing.epc_source_order != null ? Math.min(existing.epc_source_order, item.epc_source_order) : item.epc_source_order;
        existing.included_in_kits = this.uniqueKitLinks([...(existing.included_in_kits || []), ...(item.included_in_kits || [])]);
        existing.included_in_parents = this.uniqueParentLinks([...(existing.included_in_parents || []), ...(item.included_in_parents || [])]);
        existing.kit_contains_parts = Array.from(new Set([...(existing.kit_contains_parts || []), ...(item.kit_contains_parts || [])]));
        existing.epc_comments = existing.epc_comments || item.epc_comments || null;
        if (item.epc_availability === 'no_longer_available' || item.recommendation_status === 'no_longer_available') {
          existing.epc_availability = 'no_longer_available';
          existing.recommendation_status = 'no_longer_available';
          existing.customer_action = 'no_longer_available';
          existing.button_label = 'No Longer Available';
          existing.epc_comments = item.epc_comments || existing.epc_comments || null;
        }
      });

      const currentSkus = new Set(bySku.keys());

      // Hide old part card when current part exists; move chain under current.
      const supersessions = this.allSupersessions();

      Object.keys(supersessions).forEach((oldPart) => {
        const currentPart = supersessions[oldPart];
        const oldSku = this.normalizeSku(oldPart);
        const currentSku = this.normalizeSku(currentPart);

        if (bySku.has(oldSku) && bySku.has(currentSku)) {
          const currentItem = bySku.get(currentSku);
          currentItem.supersession_chain = this.uniqueSupersessionChain([
            ...(currentItem.supersession_chain || []),
            {
              old_part_number: oldSku,
              current_part_number: currentSku,
              note: 'Superseded part number.'
            }
          ]);
          bySku.delete(oldSku);
        }
      });

      return Array.from(bySku.values());
    }

    applyLiveProductLookupToResult(result, liveProductLookup) {
      if (!result || !liveProductLookup || !liveProductLookup.records) return result;

      (result.recommended_sections || []).forEach((section) => {
        (section.items || []).forEach((item) => {
          const sku = this.normalizeSku(item.wakeboss_sku || item.part_number);
          const live = liveProductLookup.records[sku];

          if (!live) return;

          item.shopify_join = live;
          item.commerce_status = live.commerce_status;
          item.shopify_product = live.shopify_product || null;

          // Preserve verify-only fitment status even if product exists.
          if (item.recommendation_status === 'dealer_verify') {
            item.customer_action = 'request_verification';
            item.button_label = 'Verify This Part';
            return;
          }

          // EPC no-longer-available state should not be overridden by a generic product match.
          if (item.recommendation_status === 'no_longer_available' || item.epc_availability === 'no_longer_available') {
            item.customer_action = 'no_longer_available';
            item.button_label = 'No Longer Available';
            return;
          }

          item.recommendation_status = live.recommendation_status || item.recommendation_status;
          item.customer_action = live.customer_action || item.customer_action;
          item.button_label = live.button_label || item.button_label;
        });
      });

      return result;
    }

    collectSkus(result) {
      const out = new Set();
      (result?.recommended_sections || []).forEach((section) => {
        (section.items || []).forEach((item) => {
          if (item.customer_action === 'not_sold_separately' || item.recommendation_status === 'not_sold_separately') return;
          const sku = this.normalizeSku(item.wakeboss_sku || item.part_number);
          if (sku) out.add(sku);
        });
      });
      return Array.from(out);
    }

    currentPartFor(partNumber) {
      const map = this.allSupersessions();
      return map[this.normalizeSku(partNumber)] || null;
    }

    allSupersessions() {
      const map = {};
      (this.supersessionMap.supersessions || []).forEach((row) => {
        if (row.old_part_number && row.current_part_number) {
          map[this.normalizeSku(row.old_part_number)] = this.normalizeSku(row.current_part_number);
        }
      });
      return map;
    }



    uniqueParentLinks(items) {
      const seen = new Set();
      const output = [];
      (items || []).forEach((item) => {
        const key = [item?.parent_part_number, item?.parent_group_id, item?.parent_type].filter(Boolean).join('|');
        if (!key || seen.has(key)) return;
        seen.add(key);
        output.push(item);
      });
      Object.assign(output, this.supersessionMap?.user_verified_supersessions || {});
      return output;
    }

    uniqueKitLinks(items) {
      const seen = new Set();
      const output = [];
      (items || []).forEach((item) => {
        const key = [item?.kit_part_number, item?.kit_group_id].filter(Boolean).join('|');
        if (!key || seen.has(key)) return;
        seen.add(key);
        output.push(item);
      });
      return output;
    }

    uniqueSupersessionChain(chain) {
      const seen = new Set();
      const out = [];
      (chain || []).forEach((entry) => {
        const key = `${entry.old_part_number}->${entry.current_part_number}`;
        if (!seen.has(key)) {
          seen.add(key);
          out.push(entry);
        }
      });
      return out;
    }

    serviceIntervalsFromRow(row) {
      const text = `${row.description || ''} ${row.comments || ''}`.toLowerCase();
      const out = [];
      if (text.includes('100 hour')) out.push('100 Hour');
      if (text.includes('300') || text.includes('300 hr') || text.includes('300 hour')) out.push('300 Hour');
      return out;
    }

    findPartRecord(sku) {
      const normalized = this.normalizeSku(sku);
      const records = this.partsIndex.records || [];
      return records.find((record) => {
        return [
          record.wakeboss_sku,
          record.core_part_number,
          record.part_number,
          record.part_number_display
        ].some((value) => this.normalizeSku(value) === normalized);
      }) || null;
    }


    getCatalogPartsPageIds(catalog) {
      const ids = new Set(catalog?.parts_pages || []);
      (catalog?.epc_categories || []).forEach((cat) => {
        (cat.parts_page_ids || []).forEach((id) => ids.add(id));
      });
      return Array.from(ids);
    }

    catalogHasMappedData(catalog) {
      const pageIds = new Set(this.getCatalogPartsPageIds(catalog));
      if (!pageIds.size) return false;

      return (this.partsPages.parts_pages || []).some((page) => {
        if (!pageIds.has(page.parts_page_id)) return false;
        if (Array.isArray(page.items) && page.items.length) return true;
        if (Array.isArray(page.parts) && page.parts.length) return true;
        return this.normalizeFilterValue(page.mapping_status).includes('captured');
      });
    }

    getMappedModelsForCatalog(catalog) {
      return (catalog?.models || []).filter((model) => this.modelHasMappedData(catalog, model));
    }

    modelHasMappedData(catalog, model) {
      if (!catalog || !model) return false;
      const pageIds = new Set(this.getCatalogPartsPageIds(catalog));
      if (!pageIds.size) return false;

      return (this.partsPages.parts_pages || []).some((page) => {
        return pageIds.has(page.parts_page_id) &&
          this.partsPageApplies(page, model) &&
          this.partsPageHasDisplayableRows(page);
      });
    }

    partsPageHasDisplayableRows(page) {
      const hiddenRoles = ['hidden_for_standard_verado', 'show_under_current_part', 'do_not_display'];
      return (page?.items || []).some((row) => {
        if (!row || hiddenRoles.includes(row.display_role)) return false;
        return Boolean(this.normalizeSku(row.current_part_number || row.part_number));
      });
    }


    normalizeSerial(value) {
      return String(value || '').toUpperCase().replace(/[^A-Z0-9]/g, '');
    }

    parseSerial(serial) {
      const clean = this.normalizeSerial(serial);
      const prefixed = clean.match(/^([0-9]+[A-Z]+)([0-9]+)$/) || clean.match(/^([A-Z]+)([0-9]+)$/);
      const numericOnly = clean.match(/^([0-9]+)$/);

      if (prefixed) {
        return { raw: clean, prefix: prefixed[1], number: parseInt(prefixed[2], 10) };
      }

      if (numericOnly) {
        return { raw: clean, prefix: '', number: parseInt(numericOnly[1], 10) };
      }

      return null;
    }

    rangeContains(range, parsedSerial) {
      const start = this.parseSerial(range.serial_start);
      const end = range.serial_end ? this.parseSerial(range.serial_end) : null;

      if (!start || !parsedSerial) return false;

      // For open-ended ranges such as 1B905505 & Up, do not allow a different
      // serial prefix like 2B to be swallowed by numeric prefix ranking. Cross-prefix
      // serials must be captured as explicit serial_examples or covered by their own
      // EPC range.
      if (!end && start.prefix && parsedSerial.prefix && start.prefix !== parsedSerial.prefix) {
        return false;
      }

      if (this.compareParsedSerials(parsedSerial, start) < 0) return false;
      if (end && this.compareParsedSerials(parsedSerial, end) > 0) return false;

      return true;
    }

    compareParsedSerials(a, b) {
      const prefixA = this.serialPrefixRank(a.prefix);
      const prefixB = this.serialPrefixRank(b.prefix);

      if (prefixA !== prefixB) return prefixA - prefixB;
      return a.number - b.number;
    }

    serialPrefixRank(prefix) {
      const clean = String(prefix || '').toUpperCase();
      const numericAlpha = clean.match(/^(\d+)([A-Z]+)$/);
      if (numericAlpha) {
        return (parseInt(numericAlpha[1], 10) * 1000000) + this.alphaRank(numericAlpha[2]);
      }
      return this.alphaRank(clean);
    }

    alphaRank(value) {
      return String(value || '').split('').reduce((total, char) => {
        const code = char.charCodeAt(0);
        if (code < 65 || code > 90) return total;
        return (total * 26) + (code - 64);
      }, 0);
    }

    filterMostSpecificCatalogs(catalogs, parsedSerial, normalizedSerial) {
      if (!Array.isArray(catalogs) || catalogs.length <= 1) return catalogs || [];

      const ranked = catalogs.map((catalog) => {
        const range = (catalog.serial_ranges || [])[0] || {};
        const start = this.parseSerial(range.serial_start);
        const end = range.serial_end ? this.parseSerial(range.serial_end) : null;
        const hasEnd = Boolean(range.serial_end);
        const rangeSize = start && end ? this.absoluteSerialDistance(start, end) : Number.POSITIVE_INFINITY;
        const hasSpecificPrecedence = range.precedence === 'specific_epc_catalog_choice' || range.precedence === 'exact_epc_branch_from_screenshot' || range.precedence === 'exact_epc_branch_from_screenshot_when_serial_example_matches';
        const exactStartMatch = Boolean(start && parsedSerial && start.prefix === parsedSerial.prefix && start.number === parsedSerial.number);
        const sameStartPrefix = Boolean(start && parsedSerial && start.prefix === parsedSerial.prefix);
        const exactSerialExampleMatch = Boolean(normalizedSerial && (catalog.serial_examples || []).map((item) => this.normalizeSerial(item)).includes(normalizedSerial));

        return {
          catalog,
          hasEnd,
          hasSpecificPrecedence,
          exactStartMatch,
          exactSerialExampleMatch,
          sameStartPrefix,
          start,
          startRank: start ? this.serialPrefixRank(start.prefix) : -1,
          startNumber: start ? start.number : -1,
          rangeSize,
          mappingRank: this.mappingRank(catalog.mapping_status)
        };
      });

      // If this exact serial has been captured from an EPC screenshot, that catalog wins.
      // Example: 3B553283 resolves to standard V8 / SeaPro Cat.# 90-8M0142572, not the Pro XS branch.
      const exactSerialExampleMatches = ranked.filter((row) => row.exactSerialExampleMatch);
      if (exactSerialExampleMatches.length) return exactSerialExampleMatches.map((row) => row.catalog);

      // If the entered serial is the exact beginning of a branch, that branch wins.
      // Examples:
      // - 2B529482 must show the Pro XS EPC branch.
      // - 3B266064 must show the V10 guide branch instead of being swallowed by an older open-ended V8 range.
      const exactStartMatches = ranked.filter((row) => row.exactStartMatch);
      if (exactStartMatches.length) return exactStartMatches.map((row) => row.catalog);

      // Bounded EPC ranges are safer than broad "and up" ranges when they match.
      const bounded = ranked.filter((row) => row.hasEnd);
      if (bounded.length) {
        let pool = bounded;
        const maxStartRank = Math.max(...pool.map((row) => row.startRank));
        pool = pool.filter((row) => row.startRank === maxStartRank);
        const maxStartNumber = Math.max(...pool.map((row) => row.startNumber));
        pool = pool.filter((row) => row.startNumber === maxStartNumber);
        const maxMapping = Math.max(...pool.map((row) => row.mappingRank));
        pool = pool.filter((row) => row.mappingRank === maxMapping);
        const minRange = Math.min(...pool.map((row) => row.rangeSize));
        return pool.filter((row) => row.rangeSize === minRange).map((row) => row.catalog);
      }

      // With only open-ended matches, keep plausible mapped candidates instead of forcing a single wrong one.
      // This is important because Mercury guide ranges overlap across families (V8, V10, Racing, etc.).
      let openPool = ranked.filter((row) => row.mappingRank >= 3);
      if (!openPool.length) openPool = ranked;

      // Prefer candidates whose starting prefix is closest to, but not after, the entered serial prefix.
      // Keep older-prefix candidates only when they are high-confidence exact EPC ranges.
      const parsedPrefixRank = parsedSerial ? this.serialPrefixRank(parsedSerial.prefix) : -1;
      const sameOrEarlier = openPool.filter((row) => row.startRank <= parsedPrefixRank);
      if (sameOrEarlier.length) openPool = sameOrEarlier;

      const highestPrefix = Math.max(...openPool.map((row) => row.startRank));
      const highConfidenceOlder = openPool.filter((row) => row.hasSpecificPrecedence && row.mappingRank >= 5);
      const newestPrefix = openPool.filter((row) => row.startRank === highestPrefix);

      // Return newest-prefix candidates plus any high-confidence older exact EPC branches that still may be valid.
      const byId = new Map();
      [...newestPrefix, ...highConfidenceOlder].forEach((row) => byId.set(row.catalog.catalog_id, row.catalog));
      return Array.from(byId.values());
    }

    absoluteSerialDistance(start, end) {
      if (!start || !end) return Number.POSITIVE_INFINITY;
      const prefixDistance = Math.abs(this.serialPrefixRank(end.prefix) - this.serialPrefixRank(start.prefix));
      return (prefixDistance * 1000000000) + Math.abs((end.number || 0) - (start.number || 0));
    }

    mappingRank(status) {
      const ranks = {
        mapped: 5,
        mapped_maintenance_kits: 5,
        partially_mapped: 4,
        epc_catalog_confirmed_researched_parts: 4,
        catalog_mapped_parts_pages_needed: 3,
        catalog_only: 2,
        catalog_only_broad_not_preferred_when_specific_range_exists: 1,
        mapped_maintenance_kits_broad_overlap_guarded: 1,
        mapped_corrected_by_todd_epc_screenshot: 6,
        mapped_service_kits_and_components: 6,
        user_epc_screenshot_verified_catalog_only: 4
      };

      return ranks[status] || 0;
    }

    normalizeSku(value) {
      if (!value) return '';
      const clean = String(value).toUpperCase().replace(/\s+/g, '');
      const prefixes = ['3302-', '35-', '46-', '47-', '48-', '57-', '97-', '98-', '33-', '26-', '27-', '30-', '91-', '89-', '64-', '53-', '21-', '23-', '25-', '18-', '19-', '17-', '16-', '15-', '14-', '13-', '12-', '11-', '10-', '6-', '5-'];

      for (const prefix of prefixes) {
        if (clean.startsWith(prefix)) return clean.slice(prefix.length);
      }

      return clean.replace(/\s+/g, '');
    }
  }

  window.WBMercuryEpcResultBuilder = WBMercuryEpcResultBuilder;
})();
